import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/AuthContext';
import { 
  Tv, Info, ShieldAlert, CheckCircle, Clock, Copy, 
  ExternalLink, Send, Reply, Calendar, PlusCircle, 
  X, CheckSquare, Square, Search, Eye, FileText, 
  BookOpen, ChevronRight, Check, ClipboardList
} from 'lucide-react';

interface CampaignItem {
  id: string;
  type: 'POST' | 'WHATSAPP' | 'EMAIL';
  platform: string;
  category: string;
  title: string;
  headline: string;
  subject?: string;
  body: string;
  recommendedAsset: string;
  qrCodeAsset: string;
  safeStatus: string;
}

interface QueueItem {
  id: string;
  item: CampaignItem;
  status: string;
  queuedAt: string;
  copiedAt: string | null;
  statusTimestamps: Record<string, string>;
  notes?: string;
  leadDetails?: {
    leadName: string;
    businessName: string;
    phone: string;
    email: string;
    notes: string;
    followUpDate?: string;
    createdAt: string;
  };
}

const TIKTOK_CAPTIONS: Record<string, string> = {
  'qr_campaign_packs_tiktok.mp4': 'Stop wasting money on print menus that change next week. 🛑 Update your prices, booking links, and quote forms instantly. Scan, track, and convert offline traffic into digital sales. 📈 Tap the link in bio to build your custom campaign pack today! #QRCampaign #DigitalMarketing #OfflineToOnline #SmallBusiness #NexoraX',
  'driver_fleet_systems_tiktok.mp4': 'Tired of dispatch chaos and missing delivery updates? 🚚 Automate your bookings, courier routes, and live tracking updates under a single system. Custom-built logistics operations for independent fleets. 🚀 Request a quote today! #FleetManagement #LogisticsAutomation #CourierApp #DriverBooking #NexoraX',
  'website_launchcare_tiktok.mp4': 'Is your website turning customers away? 💻 Get a high-converting, mobile-friendly, secure business website with hosting and maintenance included from just £25/month. Future-ready designs that sell. 🚀 Launch with 14 days priority support. #WebDesign #WebsiteMaintenance #BusinessGrowth #SmallBizTips #NexoraX',
  'qr_campaign_packs_tiktok_v2_voice.mp4': 'Turn posters into customer actions. 🛑 QR menus, booking links, and instant quote forms. Scan, track, and convert with NexoraX Digital Campaign Packs! 📈 #QRCampaign #OfflineToOnline #DigitalMarketing #SmallBusiness #NexoraX',
  'driver_fleet_systems_tiktok_v2_voice.mp4': 'Running drivers or couriers? 🚚 Custom booking, dispatch, and tracking systems built for scale. Request a quote today! #FleetManagement #LogisticsAutomation #CourierApp #DriverBooking #NexoraX',
  'website_launchcare_tiktok_v2_voice.mp4': 'Is your website outdated or slow? 💻 Websites, hosting, and support from just £25/month. Launch your business online with NexoraX! 🚀 #WebDesign #WebsiteMaintenance #BusinessGrowth #SmallBizTips #NexoraX'
};

export function CampaignDispatchConsole() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'library' | 'queue' | 'explorer' | 'gallery'>('library');
  const [files, setFiles] = useState<Record<string, string>>({});
  const [items, setItems] = useState<CampaignItem[]>([]);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [assets, setAssets] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filters & Selection
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedType, setSelectedType] = useState<string>('All');
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());

  // Gallery Filters
  const [gallerySearch, setGallerySearch] = useState<string>('');
  const [galleryType, setGalleryType] = useState<string>('All');
  const [galleryCategory, setGalleryCategory] = useState<string>('All');

  // Lead Form Modal
  const [leadModalOpen, setLeadModalOpen] = useState<boolean>(false);
  const [activeQueueItem, setActiveQueueItem] = useState<QueueItem | null>(null);
  const [leadForm, setLeadForm] = useState({
    leadName: '',
    businessName: '',
    phone: '',
    email: '',
    notes: '',
    followUpDate: ''
  });

  // Task Form Modal for Remote Workforce OS
  const [taskModalOpen, setTaskModalOpen] = useState<boolean>(false);
  const [selectedAssetForTask, setSelectedAssetForTask] = useState<any | null>(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    type: 'Canva / Video Tasks',
    platform: 'TikTok',
    priority: 'High',
    dueDate: '',
    brief: '',
    assetLinks: '',
    requiredOutput: 'Completed social post draft or uploaded video file.',
    estimatedCommission: '15.00'
  });

  // Raw File Explorer
  const [selectedFile, setSelectedFile] = useState<string>('README_START_HERE.md');

  // Copy Feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Selected Assets Picker State
  const [selectedAssets, setSelectedAssets] = useState<Record<string, { image: string, qr: string }>>({});

  // Toast message
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const [debugInfo, setDebugInfo] = useState<string>('');
  const [galleryTargetPostId, setGalleryTargetPostId] = useState<string>('');

  const getBestWowImage = (category: string) => {
    const cat = category.toLowerCase();
    
    if (cat.includes("website") || cat.includes("launchcare")) {
      return {
        image: "futuristic_web_design_promo_ad.png",
        qr: "qr-website-packages.png",
        reason: "Matched Website LaunchCare premium category. Selected high-fidelity futuristic web design layout."
      };
    }
    if (cat.includes("ai creator") || cat.includes("ugc") || cat.includes("creator support")) {
      return {
        image: "futuristic_ai_voice_assistant_promo.png",
        qr: "qr-ai-assistants.png",
        reason: "Matched AI Creator / UGC Content Support. Selected futuristic AI Voice Assistant promo poster."
      };
    }
    if (cat.includes("social media") || cat.includes("social packs")) {
      return {
        image: "futuristic_social_media_growth_ad.png",
        qr: "qr-main-website.png",
        reason: "Matched Social Media Campaign Packs. Selected high-impact futuristic growth analytics poster."
      };
    }
    if (cat.includes("qr campaign") || cat.includes("restaurant-takeaway-qr")) {
      return {
        image: "futuristic_qr_marketing_campaign_design.png",
        qr: "qr-restaurant-takeaway.png",
        reason: "Matched QR Campaign Packs. Selected immersive neon QR marketing layout."
      };
    }
    if (cat.includes("driver") || cat.includes("courier") || cat.includes("fleet")) {
      return {
        image: "futuristic_fleet_dispatch_system_ad.png",
        qr: "qr-driver-fleet-quote.png",
        reason: "Matched Driver/Courier/Fleet Systems. Selected futuristic logistics dispatch system poster."
      };
    }
    if (cat.includes("hosting") || cat.includes("maintenance") || cat.includes("email")) {
      return {
        image: "futuristic_hosting_and_maintenance_services.png",
        qr: "qr-maintenance-hosting.png",
        reason: "Matched Business Email / Hosting / Maintenance. Selected premium secure hosting nodes visual."
      };
    }
    if (cat.includes("restaurant") || cat.includes("takeaway")) {
      return {
        image: "futuristic_restaurant_ordering_experience_poster.png",
        qr: "qr-restaurant-takeaway.png",
        reason: "Matched Restaurant/Takeaway category. Selected zero-commission digital ordering experience poster."
      };
    }
    if (cat.includes("trades") || cat.includes("booking")) {
      return {
        image: "futuristic_service_booking_system_poster.png",
        qr: "qr-trades-field-service.png",
        reason: "Matched Tradesperson Booking. Selected automated service booking system poster."
      };
    }
    return {
      image: "futuristic_dashboard_in_a_neon_space_city.png",
      qr: "qr-main-website.png",
      reason: "Default General NexoraX Launch asset. Selected high-fidelity cosmic dashboard layout."
    };
  };

  const getHeaders = async () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    // 1. Staging operator staging email bypass
    const stagingEmail = localStorage.getItem("nexorax_bypassed_user_email") || 
                         localStorage.getItem("x-operator-staging-email");
    if (stagingEmail) {
      headers['x-operator-staging-email'] = stagingEmail.toLowerCase().trim();
    }
    
    // 2. Firebase token
    if (user) {
      try {
        const token = await user.getIdToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (e) {
        console.warn("Failed to retrieve ID token:", e);
      }
    }
    return headers;
  };

  const fetchCampaignData = async () => {
    setLoading(true);
    setError(null);
    setDebugInfo('');
    try {
      const headers = await getHeaders();
      setDebugInfo(`Fetching items using headers: ${JSON.stringify(Object.keys(headers))}`);

      // Fetch items & files
      const itemsRes = await fetch('/api/admin/campaign-dispatch/items', { headers });
      if (!itemsRes.ok) {
        const errText = await itemsRes.text();
        let errMsg = 'Failed to fetch campaign items';
        try {
          const errJSON = JSON.parse(errText);
          errMsg = errJSON.error || errMsg;
        } catch (e) {}
        throw new Error(`${errMsg} (Status: ${itemsRes.status})`);
      }
      const itemsData = await itemsRes.json();
      setFiles(itemsData.files || {});
      setItems(itemsData.items || []);

      // Fetch queue
      const queueRes = await fetch('/api/admin/campaign-dispatch/queue', { headers });
      if (queueRes.ok) {
        const queueData = await queueRes.json();
        setQueue(queueData.queue || []);
      } else {
        console.warn("Queue fetch failed status:", queueRes.status);
      }

      // Fetch assets gallery
      const assetsRes = await fetch('/api/admin/campaign-dispatch/assets', { headers });
      if (assetsRes.ok) {
        const assetsData = await assetsRes.json();
        setAssets(assetsData.assets || []);
      } else {
        console.warn("Assets fetch failed status:", assetsRes.status);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message);
      setDebugInfo(prev => `${prev}\nError caught: ${err.stack || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaignData();

    // 8-second loading timeout watchdog
    const timer = setTimeout(() => {
      setLoading(prev => {
        if (prev) {
          setError("Initialization timed out after 8 seconds. Please check if server is responsive.");
          setDebugInfo("Timeout triggered. Staging API did not respond within 8000ms.");
        }
        return false;
      });
    }, 8000);

    return () => clearTimeout(timer);
  }, [user]);

  // Bulk selections
  const handleSelectAllSafe = () => {
    const safeIds = items.map(i => i.id);
    setSelectedItemIds(new Set(safeIds));
    showToast(`Selected all ${safeIds.length} campaign items`);
  };

  const handleClearSelection = () => {
    setSelectedItemIds(new Set());
    showToast("Cleared selection");
  };

  const handleSelectByCategory = (categorySubstring: string) => {
    const ids = items
      .filter(i => i.category.toLowerCase().includes(categorySubstring.toLowerCase()))
      .map(i => i.id);
    setSelectedItemIds(new Set(ids));
    showToast(`Selected ${ids.length} items for ${categorySubstring}`);
  };

  const handleSelectByType = (type: 'POST' | 'WHATSAPP' | 'EMAIL') => {
    const ids = items.filter(i => i.type === type).map(i => i.id);
    setSelectedItemIds(new Set(ids));
    showToast(`Selected ${ids.length} ${type} items`);
  };

  const toggleItemSelection = (id: string) => {
    const next = new Set(selectedItemIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedItemIds(next);
  };

  // Add selected items to queue
  const handleCreateQueue = async () => {
    if (selectedItemIds.size === 0) {
      showToast("Please select at least one campaign item to queue.", "error");
      return;
    }

    try {
      const headers = await getHeaders();
      const selectedItems = items.filter(i => selectedItemIds.has(i.id));

      const res = await fetch('/api/admin/campaign-dispatch/queue', {
        method: 'POST',
        headers,
        body: JSON.stringify({ items: selectedItems })
      });

      if (!res.ok) {
        const errJSON = await res.json().catch(() => ({}));
        throw new Error(errJSON.error || `Failed to queue items (Status: ${res.status})`);
      }
      const data = await res.json();
      setQueue(data.queue || []);
      setSelectedItemIds(new Set());
      showToast(`Successfully queued ${selectedItems.length} items!`);
      setActiveTab('queue');
    } catch (err: any) {
      showToast(err.message, "error");
    }
  };

  // Update item status
  const handleUpdateStatus = async (id: string, status: string, notes?: string) => {
    try {
      const headers = await getHeaders();
      const res = await fetch(`/api/admin/campaign-dispatch/queue/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ status, notes })
      });

      if (!res.ok) {
        const errJSON = await res.json().catch(() => ({}));
        throw new Error(errJSON.error || `Failed to update status (Status: ${res.status})`);
      }
      const data = await res.json();
      setQueue(data.queue || []);
      showToast(`Status updated to "${status}"`);
    } catch (err: any) {
      showToast(err.message, "error");
    }
  };

  // Handle Clipboard Copy
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Handle Clipboard Copy with Staging Hook
  const handleCopyAction = async (queueItem: QueueItem, textType: string, text: string) => {
    copyToClipboard(text, `${queueItem.id}-${textType}`);
    if (queueItem.status === 'Ready') {
      await handleUpdateStatus(queueItem.id, 'Copied', `Copied ${textType} text`);
    }
  };

  // Handle Clipboard Copy and Open Platform
  const handleOpenPlatformAction = async (queueItem: QueueItem, textToCopy: string, urlToOpen: string, toastMessage: string) => {
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
    }
    if (urlToOpen) {
      window.open(urlToOpen, '_blank');
    }
    if (queueItem.status === 'Ready' || queueItem.status === 'Draft' || queueItem.status === 'Opened' || queueItem.status === 'Copied') {
      await handleUpdateStatus(queueItem.id, 'Copied', `Opened platform & copied text`);
    }
    showToast(toastMessage);
  };

  // Asset path helper
  const getAssetUrl = (fileName: string, isQr: boolean = false) => {
    if (!fileName) return '';
    const cleanName = fileName.replace(/^file:\/\/\/[^/]+\//, '').split('/').pop() || fileName;

    const readyToPostImages = [
      'ai-voice-assistant-square-1080x1080.png',
      'ai-voice-assistant-portrait-1080x1350.png',
      'ai-voice-assistant-landscape-1920x1080.png',
      'crm-booking-dashboard-square-1080x1080.png',
      'crm-booking-dashboard-portrait-1080x1350.png',
      'crm-booking-dashboard-landscape-1920x1080.png',
      'api-code-automation-square-1080x1080.png',
      'api-code-automation-portrait-1080x1350.png',
      'api-code-automation-landscape-1920x1080.png'
    ];

    const readyToPostRoot = [
      'nexorax-square-campaign-contact-sheet.png',
      'nexorax-landscape-campaign-contact-sheet.png'
    ];

    if (readyToPostImages.includes(cleanName)) {
      return `/campaign-assets/ready/images/${cleanName}`;
    }
    if (readyToPostRoot.includes(cleanName)) {
      return `/campaign-assets/ready/${cleanName}`;
    }

    if (isQr) {
      return `/campaign-assets/files/ALL_QR_CODES/${cleanName}`;
    } else {
      return `/campaign-assets/files/ALL_CAMPAIGN_IMAGES/${cleanName}`;
    }
  };

  // Open Asset Helper
  const handleOpenAsset = (assetName: string, isQr: boolean = false) => {
    const path = getAssetUrl(assetName, isQr);
    window.open(path, '_blank');
  };

  // Submit Lead Modal Form
  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeQueueItem) return;

    try {
      const headers = await getHeaders();
      const res = await fetch('/api/admin/campaign-dispatch/lead-note', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          queueItemId: activeQueueItem.id,
          ...leadForm
        })
      });

      if (!res.ok) {
        const errJSON = await res.json().catch(() => ({}));
        throw new Error(errJSON.error || `Failed to submit lead note (Status: ${res.status})`);
      }
      const data = await res.json();
      setQueue(data.queue || []);
      setLeadModalOpen(false);
      setLeadForm({ leadName: '', businessName: '', phone: '', email: '', notes: '', followUpDate: '' });
      showToast("Business lead captured successfully!");
    } catch (err: any) {
      showToast(err.message, "error");
    }
  };

  // Trigger task form creation modal populated with asset info
  const handleTriggerCreateTask = (asset: any) => {
    const isVideo = asset.type === 'video';
    const type = isVideo ? 'Canva / Video Tasks' : 'QR Campaign Tasks';
    const cleanTitle = `Repurpose Campaign: ${asset.title}`;
    const cleanBrief = `Repurpose asset ${asset.filename} (${asset.category}) for marketing campaigns. Check branding is correct.`;
    const assetUrl = window.location.origin + asset.public_url;
    
    // Set 7 days default due date
    const d = new Date();
    d.setDate(d.getDate() + 7);
    const dueDateStr = d.toISOString().split('T')[0];

    setTaskForm({
      title: cleanTitle,
      type,
      platform: 'TikTok',
      priority: 'High',
      dueDate: dueDateStr,
      brief: cleanBrief,
      assetLinks: assetUrl,
      requiredOutput: isVideo ? 'Completed vertical H.264 video with voiceover or Canva link.' : 'Scannable campaign image pack draft.',
      estimatedCommission: '15.00'
    });
    setSelectedAssetForTask(asset);
    setTaskModalOpen(true);
  };

  const handleTaskSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const headers = await getHeaders();
      const res = await fetch('/api/admin/workforce/tasks', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...taskForm,
          campaignCategory: selectedAssetForTask?.category || 'General NexoraX Launch',
          status: 'Available'
        })
      });
      if (!res.ok) {
        const errJSON = await res.json().catch(() => ({}));
        throw new Error(errJSON.error || `Failed to create workforce task (Status: ${res.status})`);
      }
      setTaskModalOpen(false);
      setSelectedAssetForTask(null);
      showToast("Workforce production task created successfully!");
    } catch (err: any) {
      showToast(err.message, "error");
    }
  };

  // Filter items based on search & category select
  const filteredItems = items.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          item.headline.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.body.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    const matchesType = selectedType === 'All' || item.type === selectedType;
    return matchesSearch && matchesCategory && matchesType;
  });

  // Calculate Metrics
  const queuedCount = queue.length;
  const copiedTodayCount = queue.filter(q => q.status === 'Copied' || q.status === 'Ready' && q.copiedAt).length;
  const sentTodayCount = queue.filter(q => q.status === 'Sent Manually' || q.status === 'Posted Manually').length;
  const repliesCount = queue.filter(q => q.status === 'Reply Received').length;
  const leadsCount = queue.filter(q => q.status === 'Lead Created').length;
  const followUpsCount = queue.filter(q => q.status === 'Follow Up Needed' || (q.leadDetails?.followUpDate)).length;

  // Categories list
  const categories = [
    "Website LaunchCare from £25/month",
    "AI Creator / UGC content support from £25/month as managed service",
    "Social Media Campaign Packs from £25/month as managed service",
    "QR Campaign Packs",
    "Business Email / Hosting / Maintenance",
    "Driver / Courier / Fleet Systems by quote",
    "Mobile Apps / App-style Systems by quote",
    "Diamond / Black Diamond / VIP custom systems by quote"
  ];

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center font-mono text-purple-400">
        <Clock className="mr-2 h-5 w-5 animate-spin text-purple-400" />
        INITIALIZING CAMPAIGN DISPATCH CONSOLE SYSTEM...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/30 bg-red-950/20 p-6 text-red-300">
        <ShieldAlert className="mb-2 h-8 w-8 text-red-500" />
        <h3 className="font-mono text-lg font-bold">SYSTEM BOOT FAILURE</h3>
        <p className="mt-2 text-sm">{error}</p>
        {debugInfo && (
          <pre className="mt-4 max-h-40 overflow-auto rounded bg-black/40 p-3 font-mono text-[10px] text-purple-400">
            {debugInfo}
          </pre>
        )}
        <button 
          onClick={fetchCampaignData} 
          className="mt-4 rounded bg-red-900/40 px-4 py-2 text-xs font-bold hover:bg-red-900/60"
        >
          Retry Load Campaign Items
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Alert */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg px-4 py-3 shadow-lg border transition-all ${
          toast.type === 'success' 
            ? 'bg-emerald-950/90 border-emerald-500/30 text-emerald-300' 
            : 'bg-red-950/90 border-red-500/30 text-red-300'
        }`}>
          <Info className="h-4 w-4" />
          <span className="font-mono text-xs font-black">{toast.message}</span>
        </div>
      )}

      {/* Safety Wording Warn Banner */}
      <div className="relative overflow-hidden rounded-xl border border-purple-500/30 bg-purple-950/15 p-5 shadow-[0_0_15px_rgba(168,85,247,0.1)]">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-purple-500/10 p-2 text-purple-400">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <div className="space-y-2">
            <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-purple-300">
              DIRECTOR SAFETY PROTOCOLS & COMPLIANCE WARNING
            </h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              <strong>Semi-automated mode is active.</strong> This console does not connect to external APIs or automatically send outreach. 
              All items must be copied and sent manually by the Director. 
              <strong> Wording Audit:</strong> Do not promise guaranteed views, organic growth rates, fixed income projections, or instant automated video generation. All offerings represent managed B2B services.
            </p>
          </div>
        </div>
      </div>

      {/* Workflow Helper Panel */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
        <h4 className="font-mono text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-purple-400" />
          Semi-Automated Dispatch Workflow
        </h4>
        <p className="text-xxs text-slate-400 font-mono">
          <strong>Instructions:</strong> Semi-automated mode: this console copies messages and opens platforms only. It does not post, send, email, or message automatically. Always review before posting.
        </p>
        <div className="grid gap-3 sm:grid-cols-5 text-xxs font-mono">
          {[
            { step: 'Step 1', desc: 'Select campaign item' },
            { step: 'Step 2', desc: 'Click Copy + Open platform' },
            { step: 'Step 3', desc: 'Paste / review manually' },
            { step: 'Step 4', desc: 'Press Post / Send yourself' },
            { step: 'Step 5', desc: 'Return & Mark Sent Manually' }
          ].map((s, idx) => (
            <div key={idx} className="rounded border border-slate-800/80 bg-slate-950/40 p-3 relative flex flex-col justify-between">
              <span className="text-[9px] font-bold text-purple-500/80 mb-1">{s.step}</span>
              <div className="text-slate-300 font-mono">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Campaign Control Dashboard Counters */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-6">
        {[
          { label: 'Selected Library', count: selectedItemIds.size, color: 'text-purple-400', border: 'border-purple-500/20' },
          { label: 'Queued Actions', count: queuedCount, color: 'text-cyan-400', border: 'border-cyan-500/20' },
          { label: 'Copied Today', count: copiedTodayCount, color: 'text-blue-400', border: 'border-blue-500/20' },
          { label: 'Manually Sent', count: sentTodayCount, color: 'text-indigo-400', border: 'border-indigo-500/20' },
          { label: 'Replies Received', count: repliesCount, color: 'text-emerald-400', border: 'border-emerald-500/20' },
          { label: 'Leads Created', count: leadsCount, color: 'text-yellow-400', border: 'border-yellow-500/20' }
        ].map((m, idx) => (
          <div key={idx} className={`rounded-xl border ${m.border} bg-slate-900/60 p-4 text-center backdrop-blur-md`}>
            <div className="font-mono text-xxs uppercase tracking-wider text-slate-500">{m.label}</div>
            <div className={`mt-1 font-mono text-2xl font-black ${m.color}`}>{m.count}</div>
          </div>
        ))}
      </div>

      {/* Campaign Asset Counters */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { label: 'Images Found', count: 22, color: 'text-purple-400', border: 'border-purple-500/20' },
          { label: 'QR Codes Found', count: 8, color: 'text-cyan-400', border: 'border-cyan-500/20' },
          { label: 'Campaign Files', count: 9, color: 'text-blue-400', border: 'border-blue-500/20' },
          { label: 'Missing Files', count: 0, color: 'text-emerald-400', border: 'border-emerald-500/20' }
        ].map((m, idx) => (
          <div key={idx} className={`rounded-xl border ${m.border} bg-slate-900/60 p-4 text-center backdrop-blur-md`}>
            <div className="font-mono text-xxs uppercase tracking-wider text-slate-500">{m.label}</div>
            <div className={`mt-1 font-mono text-xl font-black ${m.color}`}>{m.count}</div>
          </div>
        ))}
      </div>

      {/* Main Tabs Navigation */}
      <div className="flex border-b border-slate-800">
        {[
          { id: 'library', label: '01. Campaign Library', icon: Tv },
          { id: 'queue', label: `02. Dispatch Queue (${queuedCount})`, icon: Clock },
          { id: 'explorer', label: '03. Launch Pack Files', icon: FileText },
          { id: 'gallery', label: '04. Asset Gallery', icon: Eye }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id as any)}
            className={`flex items-center gap-2 border-b-2 px-6 py-3 font-mono text-xs font-bold uppercase tracking-wider transition-all ${
              activeTab === t.id 
                ? 'border-purple-500 bg-purple-950/10 text-purple-400' 
                : 'border-transparent text-slate-500 hover:text-slate-300'
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Panel Content */}
      <div className="min-h-[500px]">
        {activeTab === 'library' && (
          <div className="space-y-6">
            {/* Library Tools & Filters */}
            <div className="grid gap-4 md:grid-cols-4">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search campaign copy..."
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/80 py-2.5 pl-10 pr-4 font-mono text-xs text-slate-200 focus:border-purple-500 focus:outline-none"
                />
              </div>

              <div>
                <select
                  value={selectedType}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedType(e.target.value)}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-300 focus:border-purple-500 focus:outline-none"
                >
                  <option value="All">All Types</option>
                  <option value="POST">Posts (FB/LI)</option>
                  <option value="WHATSAPP">WhatsApp DMs</option>
                  <option value="EMAIL">Emails</option>
                </select>
              </div>

              <div>
                <select
                  value={selectedCategory}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedCategory(e.target.value)}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-300 focus:border-purple-500 focus:outline-none"
                >
                  <option value="All">All Offer Categories</option>
                  {categories.map((c, i) => (
                    <option key={i} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Quick Group Selection Helpers */}
            <div className="flex flex-wrap gap-2 rounded-lg border border-slate-800/80 bg-slate-950/40 p-3">
              <span className="flex items-center font-mono text-[10px] uppercase font-bold text-slate-500 mr-2">Selection Helpers:</span>
              <button 
                onClick={handleSelectAllSafe} 
                className="rounded bg-slate-800 px-3 py-1 font-mono text-xxs font-bold text-slate-300 hover:bg-slate-700"
              >
                Select All
              </button>
              <button 
                onClick={handleClearSelection} 
                className="rounded bg-slate-800 px-3 py-1 font-mono text-xxs font-bold text-slate-300 hover:bg-slate-700"
              >
                Clear All
              </button>
              <button 
                onClick={() => handleSelectByCategory("Website LaunchCare")} 
                className="rounded bg-purple-950/45 border border-purple-500/20 px-3 py-1 font-mono text-xxs font-bold text-purple-300 hover:bg-purple-950"
              >
                LaunchCare
              </button>
              <button 
                onClick={() => handleSelectByCategory("AI Creator")} 
                className="rounded bg-purple-950/45 border border-purple-500/20 px-3 py-1 font-mono text-xxs font-bold text-purple-300 hover:bg-purple-950"
              >
                AI Creator
              </button>
              <button 
                onClick={() => handleSelectByCategory("QR Campaign")} 
                className="rounded bg-purple-950/45 border border-purple-500/20 px-3 py-1 font-mono text-xxs font-bold text-purple-300 hover:bg-purple-950"
              >
                QR Campaigns
              </button>
              <button 
                onClick={() => handleSelectByCategory("Driver")} 
                className="rounded bg-purple-950/45 border border-purple-500/20 px-3 py-1 font-mono text-xxs font-bold text-purple-300 hover:bg-purple-950"
              >
                Logistics/Fleet
              </button>
              <button 
                onClick={() => handleSelectByType("WHATSAPP")} 
                className="rounded bg-emerald-950/40 border border-emerald-500/20 px-3 py-1 font-mono text-xxs font-bold text-emerald-300 hover:bg-emerald-950"
              >
                All WhatsApp DMs
              </button>
              <button 
                onClick={() => handleSelectByType("EMAIL")} 
                className="rounded bg-blue-950/40 border border-blue-500/20 px-3 py-1 font-mono text-xxs font-bold text-blue-300 hover:bg-blue-950"
              >
                All Email Templates
              </button>
            </div>

            {/* Campaign Library Grid */}
            {filteredItems.length === 0 ? (
              <div className="rounded-xl border border-slate-880 bg-slate-900/20 py-12 text-center text-slate-500 font-mono text-xs">
                NO CAMPAIGN COPY ITEMS MATCHING CURRENT FILTERS
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2">
                {filteredItems.map(item => {
                  const isSelected = selectedItemIds.has(item.id);
                  return (
                    <div 
                      key={item.id} 
                      onClick={() => toggleItemSelection(item.id)}
                      className={`relative flex flex-col justify-between rounded-xl border p-5 transition-all cursor-pointer ${
                        isSelected 
                          ? 'border-purple-500 bg-purple-950/10 shadow-[0_0_15px_rgba(168,85,247,0.05)]' 
                          : 'border-slate-800/80 bg-slate-900/40 hover:border-slate-700'
                      }`}
                    >
                      <div className="space-y-3">
                        {/* Card Header metadata */}
                        <div className="flex items-center justify-between">
                          <span className={`rounded px-2.5 py-0.5 font-mono text-[10px] font-black uppercase ${
                            item.type === 'POST' ? 'bg-purple-500/10 text-purple-400' :
                            item.type === 'WHATSAPP' ? 'bg-emerald-500/10 text-emerald-400' :
                            'bg-blue-500/10 text-blue-400'
                          }`}>
                            {item.platform}
                          </span>
                          
                          <div className="flex items-center gap-2">
                            <span className="rounded bg-slate-800 px-2 py-0.5 font-mono text-[9px] text-slate-400 uppercase">
                              {item.safeStatus}
                            </span>
                            {isSelected ? (
                              <CheckSquare className="h-4 w-4 text-purple-400" />
                            ) : (
                              <Square className="h-4 w-4 text-slate-600" />
                            )}
                          </div>
                        </div>

                        <h4 className="font-mono text-sm font-bold text-slate-200">{item.headline}</h4>
                        <p className="line-clamp-4 font-sans text-xs text-slate-400 whitespace-pre-line">{item.body}</p>
                      </div>

                      <div className="mt-4 pt-3 border-t border-slate-800/85 flex flex-col gap-2">
                        <div className="flex items-center justify-between text-xxs font-mono text-slate-500">
                          <span>{item.category}</span>
                          <span className="text-purple-400/70">Scan ID: {item.id}</span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const campaignName = `${item.headline.substring(0, 15).replace(/[^a-zA-Z0-9]/g, '_')}_${item.id}`;
                              const source = item.type === 'POST' ? 'facebook' : (item.type === 'WHATSAPP' ? 'whatsapp' : 'email');
                              const medium = item.type === 'POST' ? 'social' : (item.type === 'WHATSAPP' ? 'direct_message' : 'email');
                              try {
                                const res = await fetch("/api/admin/workforce/traffic/register", {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({
                                    campaignName,
                                    source,
                                    medium,
                                    category: item.category,
                                    destination: "homepage",
                                    destinationUrl: "https://nexoraxdigital.co.uk/"
                                  })
                                });
                                const d = await res.json();
                                if (d.success) {
                                  const trackingUrl = `${window.location.origin}/r/${d.trackingId}`;
                                  navigator.clipboard.writeText(trackingUrl);
                                  alert(`Tracking redirect URL copied to clipboard:\n${trackingUrl}`);
                                }
                              } catch (err) {
                                alert("Failed to register tracking link.");
                              }
                            }}
                            className="rounded bg-slate-900 border border-slate-800/60 hover:bg-slate-800 px-3 py-1 font-mono text-[9px] font-bold text-slate-400 hover:text-white uppercase tracking-wider cursor-pointer"
                          >
                            🔗 Create Tracking Link
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Sticky Queue Trigger Action Bar */}
            {selectedItemIds.size > 0 && (
              <div className="sticky bottom-4 flex items-center justify-between rounded-xl border border-purple-500 bg-slate-950 p-4 shadow-xl">
                <span className="font-mono text-xs text-purple-300 font-bold">
                  {selectedItemIds.size} CAMPAIGN ITEMS SELECTED FOR DISPATCH
                </span>
                <button
                  onClick={handleCreateQueue}
                  className="flex items-center gap-2 rounded-lg bg-purple-600 px-5 py-2 font-mono text-xs font-black uppercase text-white hover:bg-purple-500 transition-all"
                >
                  <PlusCircle className="h-4 w-4" />
                  Create Dispatch Queue
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'queue' && (
          <div className="space-y-6">
            {queue.length === 0 ? (
              <div className="rounded-xl border border-slate-850 bg-slate-900/20 py-16 text-center text-slate-500 font-mono text-xs">
                DISPATCH QUEUE IS EMPTY. SELECT CAMPAIGN ITEMS TO QUEUE.
              </div>
            ) : (
              <div className="space-y-4">
                {queue.map(qItem => (
                  <div key={qItem.id} className="rounded-xl border border-slate-800 bg-slate-900/35 p-5">
                    {/* Header */}
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-[10px] font-bold text-slate-500">{qItem.id}</span>
                        <h4 className="font-mono text-sm font-bold text-slate-200">{qItem.item.headline}</h4>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Status Badge */}
                        <span className={`rounded-full px-3 py-0.5 font-mono text-[10px] font-bold uppercase ${
                          qItem.status === 'Ready' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' :
                          qItem.status === 'Copied' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                          qItem.status === 'Sent Manually' || qItem.status === 'Posted Manually' ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' :
                          qItem.status === 'Reply Received' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                          qItem.status === 'Lead Created' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' :
                          'bg-slate-700/20 text-slate-400 border border-slate-700/30'
                        }`}>
                          {qItem.status}
                        </span>

                        <span className="font-mono text-[10px] text-slate-500">
                          Queued: {new Date(qItem.queuedAt).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>

                    {/* Middle: Content Preview */}
                    <div className="mt-4 grid gap-5 md:grid-cols-3">
                      <div className="md:col-span-2 space-y-3">
                        {qItem.item.type === 'EMAIL' && (
                          <div className="rounded bg-slate-950/70 p-3 border border-slate-900 font-mono text-xs space-y-2">
                            <div className="text-slate-500 font-black">
                              SUBJECT: <span className="text-slate-300">{qItem.item.subject}</span>
                            </div>
                            <div className="text-slate-500 font-black flex items-center justify-between">
                              BODY:
                              <button 
                                onClick={() => handleCopyAction(qItem, 'subject', qItem.item.subject || '')}
                                className="text-xxs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                              >
                                {copiedId === `${qItem.id}-subject` ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                Copy Subject
                              </button>
                            </div>
                            <p className="text-slate-400 max-h-48 overflow-y-auto whitespace-pre-line text-xxs custom-scrollbar">{qItem.item.body}</p>
                          </div>
                        )}

                        {qItem.item.type !== 'EMAIL' && (
                          <div className="rounded bg-slate-950/70 p-3 border border-slate-900 font-mono text-xxs">
                            <p className="text-slate-400 max-h-48 overflow-y-auto whitespace-pre-line custom-scrollbar">{qItem.item.body}</p>
                          </div>
                        )}
                      </div>

                      {/* Side: Visual assets catalog preview & picker */}
                      <div className="rounded bg-slate-950/50 p-4 border border-slate-900 space-y-4 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="block font-mono text-[10px] uppercase font-bold text-slate-500">Campaign Assets</span>
                            {['post-007', 'post-008'].includes(qItem.item.id) && (
                              <span className="rounded bg-red-950/40 border border-red-500/30 text-red-400 px-1.5 py-0.5 font-mono text-[8px] font-black uppercase">
                                INTERNAL / DIRECTOR ONLY
                              </span>
                            )}
                          </div>
                          
                          {/* Recommended assets previews */}
                          {selectedAssets[qItem.id] ? (
                            <div className="space-y-3 mt-3">
                              {/* Reason/Match explanation */}
                              <div className="rounded bg-purple-950/20 border border-purple-500/10 p-2 font-mono text-[9px] text-purple-300">
                                <div><strong>Category Match:</strong> {qItem.item.category}</div>
                                <div className="mt-1 text-[8px] text-slate-400">
                                  {getBestWowImage(qItem.item.category).reason}
                                </div>
                              </div>

                              {/* Image Preview */}
                              <div className="space-y-1">
                                <span className="block font-mono text-[8px] text-slate-500 uppercase">Selected Recommended Wow-Factor Image</span>
                                <div className="relative rounded overflow-hidden border border-slate-800 bg-black/40 h-28 flex items-center justify-center">
                                  <img 
                                    src={getAssetUrl(selectedAssets[qItem.id].image, false)} 
                                    alt="Campaign Poster" 
                                    className="object-contain h-full w-full"
                                    onError={(e) => {
                                      (e.target as HTMLElement).style.display = 'none';
                                    }}
                                  />
                                </div>
                                <span className="block font-mono text-[9px] text-slate-400 truncate">{selectedAssets[qItem.id].image}</span>
                                <div className="grid grid-cols-3 gap-1">
                                  <button
                                    onClick={() => handleOpenAsset(selectedAssets[qItem.id].image, false)}
                                    className="rounded bg-slate-800 hover:bg-slate-700 py-1 font-mono text-[9px] text-slate-300 text-center flex items-center justify-center gap-0.5"
                                  >
                                    <ExternalLink className="h-2.5 w-2.5" /> Open
                                  </button>
                                  <a
                                    href={getAssetUrl(selectedAssets[qItem.id].image, false)}
                                    download={selectedAssets[qItem.id].image}
                                    className="rounded bg-slate-800 hover:bg-slate-700 py-1 font-mono text-[9px] text-slate-300 text-center flex items-center justify-center gap-0.5"
                                  >
                                    Download
                                  </a>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(window.location.origin + getAssetUrl(selectedAssets[qItem.id].image, false));
                                      showToast("Image link copied to clipboard!");
                                    }}
                                    className="rounded bg-slate-800 hover:bg-slate-700 py-1 font-mono text-[9px] text-slate-300 text-center flex items-center justify-center gap-0.5"
                                  >
                                    <Copy className="h-2.5 w-2.5" /> Link
                                  </button>
                                </div>
                              </div>

                              {/* QR Preview */}
                              <div className="space-y-1">
                                <span className="block font-mono text-[8px] text-slate-500 uppercase">Scannable QR Code</span>
                                <div className="relative rounded overflow-hidden border border-slate-800 bg-black/40 h-20 flex items-center justify-center">
                                  <img 
                                    src={getAssetUrl(selectedAssets[qItem.id].qr, true)} 
                                    alt="QR Code" 
                                    className="object-contain h-full w-full"
                                    onError={(e) => {
                                      (e.target as HTMLElement).style.display = 'none';
                                    }}
                                  />
                                </div>
                                <span className="block font-mono text-[9px] text-slate-400 truncate">{selectedAssets[qItem.id].qr}</span>
                                <div className="grid grid-cols-3 gap-1">
                                  <button
                                    onClick={() => handleOpenAsset(selectedAssets[qItem.id].qr, true)}
                                    className="rounded bg-slate-800 hover:bg-slate-700 py-1 font-mono text-[9px] text-slate-300 text-center flex items-center justify-center gap-0.5"
                                  >
                                    <ExternalLink className="h-2.5 w-2.5" /> Open
                                  </button>
                                  <a
                                    href={getAssetUrl(selectedAssets[qItem.id].qr, true)}
                                    download={selectedAssets[qItem.id].qr}
                                    className="rounded bg-slate-800 hover:bg-slate-700 py-1 font-mono text-[9px] text-slate-300 text-center flex items-center justify-center gap-0.5"
                                  >
                                    Download
                                  </a>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(window.location.origin + getAssetUrl(selectedAssets[qItem.id].qr, true));
                                      showToast("QR link copied to clipboard!");
                                    }}
                                    className="rounded bg-slate-800 hover:bg-slate-700 py-1 font-mono text-[9px] text-slate-300 text-center flex items-center justify-center gap-0.5"
                                  >
                                    <Copy className="h-2.5 w-2.5" /> Link
                                  </button>
                                </div>
                              </div>

                              <button
                                onClick={() => {
                                  setSelectedAssets(prev => {
                                    const updated = { ...prev };
                                    delete updated[qItem.id];
                                    return updated;
                                  });
                                  showToast("Image reset. You can select another image.");
                                }}
                                className="w-full rounded bg-slate-800 hover:bg-slate-750 py-1.5 font-mono text-[9px] text-slate-300 border border-slate-700 animate-pulse"
                              >
                                Replace Image
                              </button>
                            </div>
                          ) : (
                            <div className="py-6 text-center border border-dashed border-slate-800 rounded mt-3 space-y-2">
                              <span className="block font-mono text-[9px] text-slate-500">No image selected.</span>
                              <button
                                onClick={() => {
                                  const wowMatch = getBestWowImage(qItem.item.category);
                                  setSelectedAssets(prev => ({
                                    ...prev,
                                    [qItem.id]: {
                                      image: wowMatch.image,
                                      qr: wowMatch.qr
                                    }
                                  }));
                                  showToast("Best wow-factor image automatically matched!");
                                }}
                                className="rounded bg-purple-950 border border-purple-500/25 px-2.5 py-1 font-mono text-[9px] text-purple-400 hover:bg-purple-900/40"
                              >
                                Choose Best Image For This Post
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Facebook helper instructions */}
                        {qItem.item.type === 'POST' && (
                          <div className="pt-2 border-t border-slate-900 font-mono text-[9px] text-slate-500 leading-relaxed">
                            <strong>Facebook Helper:</strong> After Facebook opens, paste your copied post text, then use Add Photo/Video to upload the selected wow-factor image.
                          </div>
                        )}

                        <div className="pt-2 border-t border-slate-900">
                          <button
                            onClick={() => window.open('/campaign-assets', '_blank')}
                            className="w-full rounded bg-slate-900 hover:bg-slate-850 py-1.5 font-mono text-[9px] text-slate-400 hover:text-slate-200 transition-all border border-slate-800"
                          >
                            Open Campaign Asset Hub
                          </button>
                        </div>

                      {/* Interactive triggers */}
                      <div className="pt-2 border-t border-slate-900 flex flex-col gap-2">
                        {qItem.item.type === 'POST' && (
                          <div className="space-y-1.5">
                            <button
                              onClick={() => handleOpenPlatformAction(
                                qItem, 
                                qItem.item.body, 
                                'https://www.facebook.com/', 
                                'Copied. Paste manually into Facebook and press Post yourself.'
                              )}
                              className="w-full flex items-center justify-center gap-1.5 rounded bg-blue-700 hover:bg-blue-600 py-2.5 font-mono text-xxs font-bold text-white transition-all"
                            >
                              <ExternalLink className="h-3 w-3" /> Copy + Open Facebook
                            </button>
                            <button
                              onClick={() => handleOpenPlatformAction(
                                qItem, 
                                qItem.item.body, 
                                'https://www.linkedin.com/feed/', 
                                'Copied. Paste manually into LinkedIn and press Post yourself.'
                              )}
                              className="w-full flex items-center justify-center gap-1.5 rounded bg-sky-700 hover:bg-sky-600 py-2.5 font-mono text-xxs font-bold text-white transition-all"
                            >
                              <ExternalLink className="h-3 w-3" /> Copy + Open LinkedIn
                            </button>
                          </div>
                        )}

                        {qItem.item.type === 'WHATSAPP' && (
                            <div className="space-y-1.5">
                              <button
                                onClick={() => handleOpenPlatformAction(
                                  qItem, 
                                  qItem.item.body, 
                                  'https://web.whatsapp.com/', 
                                  'Copied. Choose contact/status manually and press Send yourself.'
                                )}
                                className="w-full flex items-center justify-center gap-1.5 rounded bg-emerald-600 hover:bg-emerald-500 py-2.5 font-mono text-xxs font-bold text-white transition-all"
                              >
                                <ExternalLink className="h-3 w-3" /> Copy + Open WhatsApp Web
                              </button>
                              <button
                                onClick={() => handleOpenPlatformAction(
                                  qItem, 
                                  qItem.item.body, 
                                  `https://web.whatsapp.com/send?text=${encodeURIComponent(qItem.item.body)}`, 
                                  'Copied. Choose contact/status manually and press Send yourself.'
                                )}
                                className="w-full flex items-center justify-center gap-1.5 rounded bg-emerald-800 hover:bg-emerald-700 py-2 font-mono text-xxs font-bold text-emerald-100 transition-all"
                              >
                                <ExternalLink className="h-3 w-3" /> Open WhatsApp with Text (Prepared Link)
                              </button>
                            </div>
                          )}

                          {qItem.item.type === 'EMAIL' && (
                            <div className="space-y-1.5">
                              <button
                                onClick={() => handleOpenPlatformAction(
                                  qItem, 
                                  qItem.item.subject || '', 
                                  `mailto:?subject=${encodeURIComponent(qItem.item.subject || '')}&body=${encodeURIComponent(qItem.item.body)}`, 
                                  'Subject copied. Draft opened. Review and send manually.'
                                )}
                                className="w-full flex items-center justify-center gap-1.5 rounded bg-blue-600 hover:bg-blue-500 py-2.5 font-mono text-xxs font-bold text-white transition-all"
                              >
                                <Send className="h-3 w-3" /> Copy Subject + Open Email
                              </button>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(qItem.item.body);
                                  handleUpdateStatus(qItem.id, 'Copied', 'Copied body text');
                                  showToast("Body text copied to clipboard.");
                                }}
                                className="w-full flex items-center justify-center gap-1.5 rounded bg-slate-800 hover:bg-slate-700 py-2 font-mono text-xxs font-bold text-slate-200 transition-all"
                              >
                                <Copy className="h-3 w-3" /> Copy Body
                              </button>
                              <button
                                onClick={() => handleOpenPlatformAction(
                                  qItem, 
                                  '', 
                                  `mailto:?subject=${encodeURIComponent(qItem.item.subject || '')}&body=${encodeURIComponent(qItem.item.body)}`, 
                                  'Draft opened. Review and send manually.'
                                )}
                                className="w-full flex items-center justify-center gap-1.5 rounded bg-slate-900 border border-slate-700 hover:bg-slate-800 py-2 font-mono text-xxs font-bold text-slate-300 transition-all"
                              >
                                <Send className="h-3 w-3" /> Open Mail Draft
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Future API Integrations */}
                    <div className="mt-4 pt-3 border-t border-slate-800/60 space-y-2">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xxs font-mono">
                        <div className="flex flex-wrap gap-2 items-center">
                          <span className="text-slate-400 font-bold uppercase">Manual Mode Active</span>
                          <span className="rounded bg-slate-900 border border-slate-800 text-slate-500 px-2 py-0.5 font-bold uppercase">Live Posting Locked</span>
                          <span className="rounded bg-red-950/30 border border-red-500/25 text-red-400 px-2 py-0.5 font-bold uppercase">Director Approval Required</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => {
                              const mockPayload = {
                                campaignItemId: qItem.id,
                                title: qItem.item.headline,
                                body: qItem.item.body,
                                mediaAsset: qItem.item.recommendedAsset,
                                timestamp: new Date().toISOString()
                              };
                              navigator.clipboard.writeText(JSON.stringify(mockPayload, null, 2));
                              showToast("API payload copied to clipboard!");
                            }}
                            className="rounded bg-slate-900 border border-slate-800 px-2.5 py-1 text-slate-400 hover:text-slate-200 transition-all font-mono"
                          >
                            Prepare API Payload
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                const headers = await getHeaders();
                                const provider = qItem.item.type === 'POST' ? 'linkedin' : qItem.item.type === 'WHATSAPP' ? 'whatsapp' : 'meta';
                                const res = await fetch(`/api/admin/social-connectors/dry-run/${provider}`, {
                                  method: 'POST',
                                  headers,
                                  body: JSON.stringify({
                                    campaignItemId: qItem.id,
                                    title: qItem.item.headline,
                                    body: qItem.item.body,
                                    assetName: qItem.item.recommendedAsset
                                  })
                                });
                                const data = await res.json();
                                if (!res.ok) throw new Error(data.error || 'Dry run failed');
                                showToast(`Dry test payload successfully sent to ${provider.toUpperCase()} connector!`);
                              } catch (e: any) {
                                showToast(e.message, 'error');
                              }
                            }}
                            className="rounded bg-purple-950/20 border border-purple-500/35 px-2.5 py-1 text-purple-400 hover:bg-purple-950/40 transition-all font-mono"
                          >
                            Send Dry Test
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Bottom: Action Tracking controls */}
                    <div className="mt-4 pt-3 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-wrap gap-1.5">
                        {qItem.item.type === 'POST' ? (
                          <button
                            onClick={() => handleUpdateStatus(qItem.id, 'Posted Manually')}
                            className="rounded-lg bg-indigo-950/40 border border-indigo-500/25 px-3 py-1 font-mono text-xxs font-bold text-indigo-400 hover:bg-indigo-950"
                          >
                            Mark Posted Manually
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUpdateStatus(qItem.id, 'Sent Manually')}
                            className="rounded-lg bg-indigo-950/40 border border-indigo-500/25 px-3 py-1 font-mono text-xxs font-bold text-indigo-400 hover:bg-indigo-950"
                          >
                            Mark Sent Manually
                          </button>
                        )}
                        <button
                          onClick={() => handleUpdateStatus(qItem.id, 'Reply Received')}
                          className="rounded-lg bg-emerald-950/40 border border-emerald-500/25 px-3 py-1 font-mono text-xxs font-bold text-emerald-400 hover:bg-emerald-950"
                        >
                          Mark Reply Received
                        </button>
                        <button
                          onClick={() => handleUpdateStatus(qItem.id, 'Follow Up Needed')}
                          className="rounded-lg bg-red-950/40 border border-red-500/25 px-3 py-1 font-mono text-xxs font-bold text-red-400 hover:bg-red-950"
                        >
                          Follow Up Required
                        </button>
                      </div>

                      <div>
                        {qItem.leadDetails ? (
                          <div className="text-xxs font-mono text-yellow-400 bg-yellow-950/20 border border-yellow-500/25 px-3 py-1.5 rounded-lg">
                            Lead captured: <strong>{qItem.leadDetails.leadName} ({qItem.leadDetails.businessName})</strong>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setActiveQueueItem(qItem);
                              setLeadModalOpen(true);
                            }}
                            className="flex items-center gap-1 rounded-lg bg-yellow-600 hover:bg-yellow-500 px-3 py-1.5 font-mono text-xxs font-bold text-slate-900 transition-all"
                          >
                            <PlusCircle className="h-3 w-3" /> Capture Lead Details
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'explorer' && (
          <div className="grid gap-6 md:grid-cols-4">
            {/* File list */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/30 p-4 space-y-2">
              <span className="block font-mono text-xxs uppercase tracking-wider text-slate-500 mb-3">Today Launch Pack Files</span>
              {Object.keys(files).sort().map(fn => (
                <button
                  key={fn}
                  onClick={() => setSelectedFile(fn)}
                  className={`w-full flex items-center justify-between rounded-lg p-2.5 font-mono text-xxs font-bold text-left transition-all ${
                    selectedFile === fn 
                      ? 'bg-purple-950/30 text-purple-400 border border-purple-500/20' 
                      : 'text-slate-400 hover:bg-slate-900/50 border border-transparent'
                  }`}
                >
                  <span className="truncate">{fn}</span>
                  <ChevronRight className="h-3 w-3" />
                </button>
              ))}
            </div>

            {/* File Raw Text Viewer */}
            <div className="md:col-span-3 rounded-xl border border-slate-850 bg-slate-950/80 p-6 shadow-inner font-mono text-xs overflow-x-auto text-slate-300 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-900">
                <span className="text-slate-400 font-black uppercase text-xxs">Raw Content: {selectedFile}</span>
                <button 
                  onClick={() => copyToClipboard(files[selectedFile] || '', `raw-${selectedFile}`)}
                  className="rounded bg-slate-800 hover:bg-slate-700 px-3 py-1 font-mono text-xxs font-bold text-slate-300"
                >
                  {copiedId === `raw-${selectedFile}` ? 'Copied!' : 'Copy Entire File'}
                </button>
              </div>
              <pre className="whitespace-pre-wrap leading-relaxed max-h-[600px] overflow-y-auto custom-scrollbar text-slate-300">{files[selectedFile] || 'No file selected.'}</pre>
            </div>
          </div>
        )}

        {activeTab === 'gallery' && (
          <div className="space-y-6">
            {/* Gallery Instructions */}
            <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-4 font-mono text-xxs text-slate-400 leading-relaxed">
              <strong>Posting Guide:</strong> To add these images or QR codes to social platforms like Facebook or LinkedIn: click <strong>Download Image</strong> or <strong>Download QR</strong>, then inside Facebook/LinkedIn click <strong>Add Photo/Video</strong> and select the downloaded file from your local storage.
            </div>
            {/* Quick Filter Buttons */}
            <div className="flex flex-wrap gap-2 rounded-lg border border-slate-800 bg-slate-950/40 p-3 font-mono text-xxs font-bold">
              <span className="flex items-center text-slate-500 uppercase mr-1">Quick Filters:</span>
              <button
                onClick={() => {
                  setGallerySearch('futuristic_');
                  setGalleryType('All');
                  setGalleryCategory('All');
                  showToast("Filtered by Premium Wow-Factor images");
                }}
                className={`rounded px-3 py-1 border transition-all ${
                  gallerySearch === 'futuristic_' 
                    ? 'bg-purple-950 border-purple-500 text-purple-300' 
                    : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850'
                }`}
              >
                Show Wow-Factor Only
              </button>
              <button
                onClick={() => {
                  setGallerySearch('');
                  setGalleryCategory('Website LaunchCare');
                  showToast("Filtered by Website LaunchCare category");
                }}
                className={`rounded px-3 py-1 border transition-all ${
                  galleryCategory === 'Website LaunchCare' 
                    ? 'bg-purple-950 border-purple-500 text-purple-300' 
                    : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850'
                }`}
              >
                Show Website Images
              </button>
              <button
                onClick={() => {
                  setGallerySearch('');
                  setGalleryCategory('AI Creator / UGC Content Support');
                  showToast("Filtered by AI Creator / UGC category");
                }}
                className={`rounded px-3 py-1 border transition-all ${
                  galleryCategory === 'AI Creator / UGC Content Support' 
                    ? 'bg-purple-950 border-purple-500 text-purple-300' 
                    : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850'
                }`}
              >
                Show AI/UGC Images
              </button>
              <button
                onClick={() => {
                  setGallerySearch('');
                  setGalleryCategory('QR Campaign Packs');
                  showToast("Filtered by QR Campaign category");
                }}
                className={`rounded px-3 py-1 border transition-all ${
                  galleryCategory === 'QR Campaign Packs' 
                    ? 'bg-purple-950 border-purple-500 text-purple-300' 
                    : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850'
                }`}
              >
                Show QR Images
              </button>
              <button
                onClick={() => {
                  setGallerySearch('');
                  setGalleryCategory('Driver / Courier / Fleet Systems');
                  showToast("Filtered by Driver/Fleet category");
                }}
                className={`rounded px-3 py-1 border transition-all ${
                  galleryCategory === 'Driver / Courier / Fleet Systems' 
                    ? 'bg-purple-950 border-purple-500 text-purple-300' 
                    : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850'
                }`}
              >
                Show Driver/Fleet Images
              </button>
              <button
                onClick={() => {
                  setGallerySearch('');
                  setGalleryCategory('Business Email / Hosting / Maintenance');
                  showToast("Filtered by Hosting/Email category");
                }}
                className={`rounded px-3 py-1 border transition-all ${
                  galleryCategory === 'Business Email / Hosting / Maintenance' 
                    ? 'bg-purple-950 border-purple-500 text-purple-300' 
                    : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850'
                }`}
              >
                Show Hosting/Email Images
              </button>
              <button
                onClick={() => {
                  setGallerySearch('');
                  setGalleryCategory('General NexoraX Launch');
                  showToast("Filtered by General Launch category");
                }}
                className={`rounded px-3 py-1 border transition-all ${
                  galleryCategory === 'General NexoraX Launch' 
                    ? 'bg-purple-950 border-purple-500 text-purple-300' 
                    : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850'
                }`}
              >
                Show General Launch Images
              </button>
              <button
                onClick={() => {
                  setGallerySearch('');
                  setGalleryType('video');
                  setGalleryCategory('All');
                  showToast("Filtered by TikTok Ready Videos");
                }}
                className={`rounded px-3 py-1 border transition-all ${
                  galleryType === 'video' 
                    ? 'bg-purple-950 border-purple-500 text-purple-300' 
                    : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850'
                }`}
              >
                Show TikTok Videos
              </button>
              <button
                onClick={() => {
                  setGallerySearch('');
                  setGalleryType('All');
                  setGalleryCategory('All');
                  showToast("Filters reset");
                }}
                className="rounded px-3 py-1 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white"
              >
                Reset Filters
              </button>
            </div>

            {/* Asset Gallery Filter Bar */}
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <span className="block font-mono text-[10px] uppercase font-bold text-slate-500 mb-1.5">Search Assets</span>
                <input
                  type="text"
                  placeholder="Filter by filename or title..."
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-200 focus:border-purple-500 focus:outline-none"
                  value={gallerySearch}
                  onChange={(e) => setGallerySearch(e.target.value)}
                  id="asset-search-input"
                />
              </div>
              
              <div>
                <span className="block font-mono text-[10px] uppercase font-bold text-slate-500 mb-1.5">Asset Type</span>
                <select
                  id="asset-type-select"
                  value={galleryType}
                  onChange={(e) => setGalleryType(e.target.value)}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-300 focus:border-purple-500 focus:outline-none"
                >
                  <option value="All">All Types</option>
                  <option value="image">Campaign Images</option>
                  <option value="qr">Scannable QR Codes</option>
                  <option value="video">Videos / MP4</option>
                </select>
              </div>

              <div>
                <span className="block font-mono text-[10px] uppercase font-bold text-slate-500 mb-1.5">Category</span>
                <select
                  id="asset-category-select"
                  value={galleryCategory}
                  onChange={(e) => setGalleryCategory(e.target.value)}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-300 focus:border-purple-500 focus:outline-none"
                >
                  <option value="All">All Categories</option>
                  <option value="TikTok Ready Videos">TikTok Ready Videos</option>
                  <option value="Website LaunchCare">Website LaunchCare</option>
                  <option value="AI Creator / UGC Content Support">AI Creator / UGC Content Support</option>
                  <option value="Social Media Campaign Packs">Social Media Campaign Packs</option>
                  <option value="QR Campaign Packs">QR Campaign Packs</option>
                  <option value="Driver / Courier / Fleet Systems">Driver / Courier / Fleet Systems</option>
                  <option value="Business Email / Hosting / Maintenance">Business Email / Hosting / Maintenance</option>
                  <option value="Mobile App / App-style Systems">Mobile App / App-style Systems</option>
                  <option value="General NexoraX Launch">General NexoraX Launch</option>
                </select>
              </div>
            </div>

            {/* Dashboard Counters */}
            <div className="flex gap-4 font-mono text-[10px] uppercase font-bold text-slate-500 pt-2 pb-1">
              <span>Videos Found: <span className="text-cyan-400">{assets.filter(a => a.type === 'video').length}</span></span>
              <span>•</span>
              <span>Images & QRs Found: <span className="text-purple-400">{assets.filter(a => a.type !== 'video').length}</span></span>
            </div>

            {/* TikTok Ready Videos Section */}
            {(galleryType === 'All' || galleryType === 'video') && assets.filter(a => {
              const matchesSearch = a.title.toLowerCase().includes(gallerySearch.toLowerCase()) || 
                                    a.filename.toLowerCase().includes(gallerySearch.toLowerCase());
              const matchesCategory = galleryCategory === 'All' || a.category === galleryCategory;
              return a.type === 'video' && matchesSearch && matchesCategory;
            }).length > 0 && (
              <div className="space-y-4 border border-slate-800 bg-slate-950/20 rounded-xl p-4">
                <div className="border-b border-slate-800 pb-2">
                  <h3 className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse"></span>
                    TikTok Ready Videos
                  </h3>
                </div>
                <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-3">
                  {assets.filter(a => {
                    const matchesSearch = a.title.toLowerCase().includes(gallerySearch.toLowerCase()) || 
                                          a.filename.toLowerCase().includes(gallerySearch.toLowerCase());
                    const matchesCategory = galleryCategory === 'All' || a.category === galleryCategory;
                    return a.type === 'video' && matchesSearch && matchesCategory;
                  }).map(asset => (
                    <div key={asset.asset_id} className="rounded-xl border border-slate-800 bg-slate-900/35 p-4 flex flex-col justify-between space-y-4">
                      <div className="space-y-3">
                        {/* Preview Panel */}
                        <div className="relative rounded overflow-hidden border border-slate-850 bg-black/40 h-56 flex items-center justify-center w-full">
                          <video 
                            src={asset.public_url} 
                            className="object-contain h-full w-full"
                            controls
                            preload="metadata"
                            muted
                            playsInline
                          />
                        </div>

                        {/* Header info */}
                        <div className="space-y-1.5">
                          <h4 className="font-mono text-xs font-bold text-slate-200 truncate">{asset.title}</h4>
                          <div className="flex justify-between items-center text-[9px] font-mono text-slate-400">
                            <span>Category: {asset.category}</span>
                            <span className="bg-slate-850 px-1.5 py-0.5 rounded text-slate-300">15s</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {asset.tags && asset.tags.map((t: string) => (
                              <span key={t} className="rounded border px-1.5 py-0.5 font-mono text-[7px] font-bold uppercase bg-purple-950/40 border-purple-500/30 text-purple-400 font-extrabold">
                                {t}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="pt-2 border-t border-slate-900 space-y-2">
                        <div className="grid grid-cols-2 gap-1.5 font-mono text-[10px]">
                          <button
                            onClick={() => window.open(asset.public_url, '_blank')}
                            className="rounded bg-slate-800 hover:bg-slate-700 py-1.5 text-center text-slate-300 font-bold"
                          >
                            Open Video
                          </button>
                          <a
                            href={asset.public_url}
                            download={asset.filename}
                            className="rounded bg-slate-850 hover:bg-slate-800 py-1.5 text-center text-slate-300 font-bold flex items-center justify-center"
                          >
                            Download Video
                          </a>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5 font-mono text-[10px]">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(window.location.origin + asset.public_url);
                              showToast("Copied video download link to clipboard!");
                            }}
                            className="rounded bg-slate-900 hover:bg-slate-850 py-1.5 text-center text-slate-400 hover:text-slate-200 border border-slate-800/80 font-bold"
                          >
                            Copy Link
                          </button>
                          <button
                            onClick={async () => {
                              const filename = asset.filename;
                              const campaignName = `TikTok_${filename.replace('.mp4', '')}`;
                              
                              let category = "qr_campaign";
                              if (filename.includes("driver")) category = "driver_fleet";
                              if (filename.includes("launchcare")) category = "website_launchcare";
                              
                              let dest = "homepage";
                              let destUrl = "https://nexoraxdigital.co.uk/";
                              if (category === 'website_launchcare') {
                                dest = "pricing";
                                destUrl += "#pricing";
                              } else if (category === 'driver_fleet') {
                                dest = "driver-ops";
                                destUrl = "https://nexoraxdigital.co.uk/driver-ops";
                              }

                              try {
                                const res = await fetch("/api/admin/workforce/traffic/register", {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({
                                    campaignName,
                                    source: "tiktok",
                                    medium: "short_video",
                                    category,
                                    destination: dest,
                                    destinationUrl: destUrl
                                  })
                                });
                                const d = await res.json();
                                if (d.success) {
                                  const trackingUrl = `${window.location.origin}/r/${d.trackingId}`;
                                  const staticCap = TIKTOK_CAPTIONS[filename] || "";
                                  let finalCap = staticCap;
                                  if (staticCap.includes("#")) {
                                    const parts = staticCap.split("#");
                                    const firstPart = parts[0].trim();
                                    const hashtags = parts.slice(1).map(p => `#${p.trim()}`).join(" ");
                                    finalCap = `${firstPart} Apply at: ${trackingUrl} 🚀 ${hashtags}`;
                                  } else {
                                    finalCap = `${staticCap} Apply at: ${trackingUrl}`;
                                  }
                                  navigator.clipboard.writeText(finalCap);
                                  showToast("Copied TikTok caption with UTM tracking link!");
                                  alert(`Tracking Link registered!\nRedirect: ${trackingUrl}\n\nFull Caption Copied to Clipboard:\n"${finalCap}"`);
                                }
                              } catch (err) {
                                navigator.clipboard.writeText(TIKTOK_CAPTIONS[filename] || "");
                                showToast("Copied TikTok caption (fallback)!");
                              }
                            }}
                            className="rounded bg-purple-950/60 hover:bg-purple-900/70 border border-purple-800/80 py-1.5 text-center text-purple-300 hover:text-purple-100 font-bold"
                          >
                            Copy Caption
                          </button>
                        </div>
                        <button
                          onClick={async () => {
                            const filename = asset.filename;
                            const campaignName = `TikTok_${filename.replace('.mp4', '')}`;
                            let category = "qr_campaign";
                            if (filename.includes("driver")) category = "driver_fleet";
                            if (filename.includes("launchcare")) category = "website_launchcare";
                            let dest = "homepage";
                            let destUrl = "https://nexoraxdigital.co.uk/";
                            if (category === 'website_launchcare') {
                              dest = "pricing";
                              destUrl += "#pricing";
                            } else if (category === 'driver_fleet') {
                              dest = "driver-ops";
                              destUrl = "https://nexoraxdigital.co.uk/driver-ops";
                            }

                            try {
                              const res = await fetch("/api/admin/workforce/traffic/register", {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({
                                    campaignName,
                                    source: "tiktok",
                                    medium: "short_video",
                                    category,
                                    destination: dest,
                                    destinationUrl: destUrl
                                  })
                              });
                              const d = await res.json();
                              if (d.success) {
                                const trackingUrl = `${window.location.origin}/r/${d.trackingId}`;
                                navigator.clipboard.writeText(trackingUrl);
                                showToast("Copied TikTok tracking redirect link!");
                                alert(`Staging Redirect link copied:\n${trackingUrl}`);
                              }
                            } catch (e) {}
                          }}
                          className="w-full rounded bg-slate-900 hover:bg-slate-850 border border-slate-800/85 py-1 text-center text-slate-400 font-bold font-mono text-[9px] uppercase tracking-wider mt-1.5 cursor-pointer"
                        >
                          🔗 Create Tracking Link
                        </button>
                        <button
                          onClick={() => handleTriggerCreateTask(asset)}
                          className="w-full rounded bg-cyan-950/60 hover:bg-cyan-900/70 border border-cyan-800/85 py-1.5 text-center text-cyan-300 hover:text-cyan-100 font-bold font-mono text-[9px] uppercase tracking-wider mt-1"
                        >
                          Create Worker Task
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Campaign Images & QR Codes Section */}
            {(galleryType === 'All' || galleryType !== 'video') && assets.filter(a => {
              const matchesSearch = a.title.toLowerCase().includes(gallerySearch.toLowerCase()) || 
                                    a.filename.toLowerCase().includes(gallerySearch.toLowerCase());
              const matchesType = galleryType === 'All' || a.type === galleryType;
              const matchesCategory = galleryCategory === 'All' || a.category === galleryCategory;
              return a.type !== 'video' && matchesSearch && matchesType && matchesCategory;
            }).length > 0 && (
              <div className="space-y-4 pt-4">
                <div className="border-b border-slate-800 pb-2">
                  <h3 className="text-xs font-mono font-bold text-purple-400 uppercase tracking-wider">
                    Campaign Images & QR Codes
                  </h3>
                </div>
                <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-4">
                  {assets.filter(asset => {
                    const matchesSearch = asset.title.toLowerCase().includes(gallerySearch.toLowerCase()) || 
                                          asset.filename.toLowerCase().includes(gallerySearch.toLowerCase());
                    const matchesType = galleryType === 'All' || asset.type === galleryType;
                    const matchesCategory = galleryCategory === 'All' || asset.category === galleryCategory;
                    return asset.type !== 'video' && matchesSearch && matchesType && matchesCategory;
                  }).map(asset => {
                    return (
                      <div key={asset.asset_id} className="rounded-xl border border-slate-800 bg-slate-900/35 p-4 flex flex-col justify-between space-y-4">
                        <div className="space-y-3">
                          {/* Preview Panel */}
                          <div className="relative rounded overflow-hidden border border-slate-850 bg-black/40 h-32 flex items-center justify-center w-full">
                            <img 
                              src={asset.public_url} 
                              alt={asset.title} 
                              className="object-contain max-h-full max-w-full"
                              onError={(e) => {
                                (e.target as HTMLElement).style.display = 'none';
                              }}
                            />
                          </div>

                          {/* Header info */}
                          <div className="space-y-1.5">
                            <h4 className="font-mono text-xs font-bold text-slate-200 truncate">{asset.title}</h4>
                            <div className="flex flex-wrap gap-1">
                              {asset.tags && asset.tags.map((t: string) => (
                                <span key={t} className={`rounded border px-1.5 py-0.5 font-mono text-[7px] font-bold uppercase truncate ${
                                  t === 'WOW FACTOR' ? 'bg-purple-950/40 border-purple-500/30 text-purple-400 font-extrabold' :
                                  t === 'APPROVED' ? 'bg-emerald-950/40 border-emerald-500/30 text-emerald-400' :
                                  t === 'RECOMMENDED' ? 'bg-blue-950/40 border-blue-500/30 text-blue-400' :
                                  t === 'QR CODE' ? 'bg-yellow-950/40 border-yellow-500/30 text-yellow-400' :
                                  t === 'BASIC FALLBACK' ? 'bg-slate-950/40 border-slate-800 text-slate-500' :
                                  'bg-red-950/40 border-red-500/30 text-red-400'
                                }`}>
                                  {t}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="pt-2 border-t border-slate-900 space-y-2">
                          <div className="grid grid-cols-2 gap-1 font-mono text-[10px]">
                            <button
                              onClick={() => window.open(asset.public_url, '_blank')}
                              className="rounded bg-slate-800 hover:bg-slate-700 py-1.5 text-center text-slate-300 font-bold"
                            >
                              Open
                            </button>
                            <a
                              href={asset.public_url}
                              download={asset.filename}
                              className="rounded bg-slate-850 hover:bg-slate-800 py-1.5 text-center text-slate-300 font-bold flex items-center justify-center"
                            >
                              Download
                            </a>
                          </div>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(window.location.origin + asset.public_url);
                              showToast("Copied asset public URL link to clipboard!");
                            }}
                            className="w-full rounded bg-slate-900 hover:bg-slate-850 py-1.5 font-mono text-[9px] text-slate-400 hover:text-slate-200 border border-slate-800/80"
                          >
                            Copy Public Link
                          </button>
                          <button
                            onClick={() => handleTriggerCreateTask(asset)}
                            className="w-full rounded bg-cyan-950/60 hover:bg-cyan-900/70 border border-cyan-800/85 py-1.5 text-center text-cyan-300 hover:text-cyan-100 font-bold font-mono text-[9px] uppercase tracking-wider mt-1.5"
                          >
                            Create Worker Task
                          </button>
                        </div>

                      {/* Attach to queued item */}
                      <div className="pt-1.5 border-t border-slate-900/50 space-y-1">
                        <label className="block font-mono text-[8px] text-slate-500 uppercase">Attach to Queued Item</label>
                        <div className="flex gap-1">
                          <select
                            onChange={(e) => setGalleryTargetPostId(e.target.value)}
                            value={galleryTargetPostId}
                            className="flex-1 rounded border border-slate-850 bg-black/60 px-1 py-1 font-mono text-[9px] text-slate-300 focus:outline-none"
                          >
                            <option value="">-- Select --</option>
                            {queue.map(q => (
                              <option key={q.id} value={q.id}>{q.id}: {q.item.title}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => {
                              if (!galleryTargetPostId) {
                                showToast("Please select a target post from the dropdown first!", "error");
                                return;
                              }
                              const targetPost = queue.find(q => q.id === galleryTargetPostId);
                              const targetQr = targetPost ? targetPost.item.qrCodeAsset : 'qr-main-website.png';
                              setSelectedAssets(prev => ({
                                ...prev,
                                [galleryTargetPostId]: {
                                  image: asset.filename,
                                  qr: targetQr
                                }
                              }));
                              showToast(`Attached ${asset.filename} to post ${galleryTargetPostId}!`);
                            }}
                            className="rounded bg-purple-950 border border-purple-500/20 px-2 py-1 font-mono text-[8px] font-black text-purple-400 hover:bg-purple-900/30"
                          >
                            Use For Post
                          </button>
                        </div>
                      </div>
                    </div>
                );
              })}
            </div>
          </div>
        )}
            {/* Generate More Wow-Factor Prompts */}
            <div className="pt-6 border-t border-slate-800 space-y-4">
              <div className="space-y-1">
                <h3 className="font-mono text-xs font-black text-purple-400 uppercase tracking-wider flex items-center gap-1.5">
                  ✨ Generate More Wow-Factor Prompts
                </h3>
                <p className="font-mono text-[10px] text-slate-500">
                  Ready-to-copy Midjourney/DALL-E 3 prompt templates to create unified premium launch packs for missing categories.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                {[
                  {
                    category: "Website LaunchCare",
                    prompt: "A sleek futuristic B2B website mockup shown on a dark holographic screen, neon purple and cyan lighting, high-fidelity UI, clean modern layout, cinematic studio lighting, 8k resolution, photorealistic --ar 16:9"
                  },
                  {
                    category: "AI Creator / UGC Support",
                    prompt: "A futuristic AI avatar generator studio console, glowing neon soundwave interfaces, sleek microphones and digital video cameras, creative workspace, synthwave aesthetic, 3d render style --ar 1:1"
                  },
                  {
                    category: "QR Campaign Packs",
                    prompt: "A modern smart-dining table in a neon Dunfermline restaurant, high-contrast QR code poster on the table, a phone scanning the code displaying a zero-commission menu, glowing neon details, photorealistic --ar 1:1"
                  },
                  {
                    category: "Driver / Fleet Systems",
                    prompt: "A futuristic logistics fleet command center dashboard, holographic maps showing delivery truck routes, glowing cyan grid overlays, sleek operator workspace, dark premium corporate design --ar 16:9"
                  },
                  {
                    category: "Hosting / Maintenance",
                    prompt: "A secure cloud hosting server array rack glowing with violet LED lights, locked neon padlock icons floating, cyber security shield interface, high tech server room, ultra realistic --ar 1:1"
                  },
                  {
                    category: "Mobile Apps",
                    prompt: "A smartphone displaying a high-contrast custom booking web app, clean B2B ledger layout, sleek interfaces, isolated on a dark futuristic workspace with subtle ambient neon backlights --ar 4:5"
                  },
                  {
                    category: "Remote Workforce OS preview",
                    prompt: "A collaborative multi-user workspace dashboard, glowing node linkages, neon network connections, futuristic admin control console, premium black theme, synthwave mood --ar 16:9"
                  },
                  {
                    category: "Social Media Campaign Packs",
                    prompt: "A high-impact social media campaign performance chart, glowing neon upward bars, digital marketing analytics dashboard, aesthetic dark corporate workspace --ar 1:1"
                  },
                  {
                    category: "Restaurant / Takeaway QR",
                    prompt: "A futuristic restaurant takeaway ordering counter, glowing zero-commission checkout screen, smartphone scanning a tabletop QR code, vibrant purple and gold hues --ar 4:5"
                  },
                  {
                    category: "Trades / Booking Systems",
                    prompt: "An automated booking calculator app interface displayed on a tablet, tradesperson tools in background, clean dark theme UI, glowing neon purple accent highlights --ar 1:1"
                  }
                ].map((item, idx) => (
                  <div key={idx} className="rounded-lg border border-slate-850 bg-slate-950/60 p-3.5 space-y-2.5 flex flex-col justify-between">
                    <div className="space-y-1">
                      <span className="block font-mono text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                        {item.category}
                      </span>
                      <p className="font-mono text-[10px] text-slate-500 italic bg-black/40 p-2 rounded border border-slate-900/60 leading-normal max-h-24 overflow-y-auto custom-scrollbar select-all">
                        {item.prompt}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(item.prompt);
                        showToast(`Prompt copied for ${item.category}!`);
                      }}
                      className="w-full rounded bg-slate-900 hover:bg-slate-800 py-1.5 font-mono text-[9px] font-black text-slate-400 hover:text-slate-200 transition-all border border-slate-850"
                    >
                      Copy Prompt Template
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lead capture modal form */}
      {leadModalOpen && activeQueueItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg rounded-xl border border-slate-800 bg-slate-900 p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="font-mono text-sm font-bold text-yellow-400 uppercase tracking-wider flex items-center gap-2">
                <PlusCircle className="h-4 w-4" /> Capture B2B Business Lead
              </h3>
              <button 
                onClick={() => setLeadModalOpen(false)}
                className="text-slate-500 hover:text-slate-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleLeadSubmit} className="space-y-4">
              <div className="font-mono text-xxs text-slate-400">
                Associating lead details with queue item ID: <strong className="text-slate-300">{activeQueueItem.id}</strong>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Lead Name</label>
                  <input
                    type="text"
                    required
                    value={leadForm.leadName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLeadForm({ ...leadForm, leadName: e.target.value })}
                    className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-200 focus:border-purple-500 focus:outline-none"
                    placeholder="e.g. John Doe"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Business Name</label>
                  <input
                    type="text"
                    required
                    value={leadForm.businessName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLeadForm({ ...leadForm, businessName: e.target.value })}
                    className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-200 focus:border-purple-500 focus:outline-none"
                    placeholder="e.g. Apex Delivery Ltd"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Phone Number</label>
                  <input
                    type="text"
                    required
                    value={leadForm.phone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLeadForm({ ...leadForm, phone: e.target.value })}
                    className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-200 focus:border-purple-500 focus:outline-none"
                    placeholder="e.g. 07123 456789"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Email Address</label>
                  <input
                    type="email"
                    required
                    value={leadForm.email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLeadForm({ ...leadForm, email: e.target.value })}
                    className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-200 focus:border-purple-500 focus:outline-none"
                    placeholder="e.g. john@apex.com"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Follow-up Date (Optional)</label>
                <input
                  type="date"
                  value={leadForm.followUpDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLeadForm({ ...leadForm, followUpDate: e.target.value })}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-200 focus:border-purple-500 focus:outline-none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Interaction & Deal Notes</label>
                <textarea
                  value={leadForm.notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setLeadForm({ ...leadForm, notes: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-200 focus:border-purple-500 focus:outline-none"
                  placeholder="Summarize client requirements, price estimates, preferred contact route, or other notes..."
                />
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-800 pt-3">
                <button
                  type="button"
                  onClick={() => setLeadModalOpen(false)}
                  className="rounded-lg bg-slate-800 px-4 py-2 font-mono text-xs font-bold text-slate-400 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-yellow-600 hover:bg-yellow-500 px-5 py-2 font-mono text-xs font-black text-slate-900"
                >
                  Save Lead & Log Note
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task Creation Modal Form for Remote Workforce OS */}
      {taskModalOpen && selectedAssetForTask && (
        <div className="fixed inset-0 z-55 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="w-full max-w-xl rounded-xl border border-slate-800 bg-slate-900 p-6 shadow-2xl space-y-4 my-8 max-h-[90vh] overflow-y-auto text-left">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="font-mono text-sm font-bold text-cyan-400 uppercase tracking-wider flex items-center gap-2">
                <ClipboardList className="h-4 w-4" /> Create Workforce Production Task
              </h3>
              <button 
                onClick={() => setTaskModalOpen(false)}
                className="text-slate-500 hover:text-slate-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleTaskSubmit} className="space-y-4">
              <div className="space-y-1">
                <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Task Title</label>
                <input
                  type="text"
                  required
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-200 focus:border-cyan-500 focus:outline-none"
                  placeholder="e.g. Canva Video Repurpose"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1">
                  <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Task Type</label>
                  <select
                    value={taskForm.type}
                    onChange={(e) => setTaskForm({ ...taskForm, type: e.target.value })}
                    className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-300 focus:border-cyan-500 focus:outline-none"
                  >
                    <option value="Campaign Tasks">Campaign Tasks</option>
                    <option value="Canva / Video Tasks">Canva / Video Tasks</option>
                    <option value="TikTok / Shorts Tasks">TikTok / Shorts Tasks</option>
                    <option value="QR Campaign Tasks">QR Campaign Tasks</option>
                    <option value="SEO Tasks">SEO Tasks</option>
                    <option value="Social Media Tasks">Social Media Tasks</option>
                    <option value="Email Campaign Tasks">Email Campaign Tasks</option>
                    <option value="Website Template Tasks">Website Template Tasks</option>
                    <option value="Digital Product Tasks">Digital Product Tasks</option>
                    <option value="Lead Research Tasks">Lead Research Tasks</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Target Platform</label>
                  <select
                    value={taskForm.platform}
                    onChange={(e) => setTaskForm({ ...taskForm, platform: e.target.value })}
                    className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-300 focus:border-cyan-500 focus:outline-none"
                  >
                    <option value="TikTok">TikTok</option>
                    <option value="YouTube Shorts">YouTube Shorts</option>
                    <option value="Instagram Reels">Instagram Reels</option>
                    <option value="Facebook Reels">Facebook Reels</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Facebook Post">Facebook Post</option>
                    <option value="WhatsApp Status">WhatsApp Status</option>
                    <option value="Google Business Profile">Google Business Profile</option>
                    <option value="Email Outreach">Email Outreach</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Priority</label>
                  <select
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                    className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-300 focus:border-cyan-500 focus:outline-none"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Due Date</label>
                  <input
                    type="date"
                    required
                    value={taskForm.dueDate}
                    onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                    className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-200 focus:border-cyan-500 focus:outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Est. Commission (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={taskForm.estimatedCommission}
                    onChange={(e) => setTaskForm({ ...taskForm, estimatedCommission: e.target.value })}
                    className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-200 focus:border-cyan-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Campaign Asset URL</label>
                <input
                  type="text"
                  required
                  value={taskForm.assetLinks}
                  onChange={(e) => setTaskForm({ ...taskForm, assetLinks: e.target.value })}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-200 focus:border-cyan-500 focus:outline-none"
                  placeholder="Asset URL link"
                />
              </div>

              <div className="space-y-1">
                <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Task Brief & Guidelines</label>
                <textarea
                  value={taskForm.brief}
                  onChange={(e) => setTaskForm({ ...taskForm, brief: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-200 focus:border-cyan-500 focus:outline-none"
                  placeholder="Details of what the worker needs to do..."
                />
              </div>

              <div className="space-y-1">
                <label className="block font-mono text-xxs uppercase tracking-wider text-slate-500">Required Deliverable Output</label>
                <input
                  type="text"
                  required
                  value={taskForm.requiredOutput}
                  onChange={(e) => setTaskForm({ ...taskForm, requiredOutput: e.target.value })}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/80 p-2.5 font-mono text-xs text-slate-200 focus:border-cyan-500 focus:outline-none"
                  placeholder="e.g. Canva design template link or vertical MP4 file upload link"
                />
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-800 pt-3">
                <button
                  type="button"
                  onClick={() => setTaskModalOpen(false)}
                  className="rounded-lg bg-slate-800 px-4 py-2 font-mono text-xs font-bold text-slate-400 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-cyan-600 hover:bg-cyan-500 px-5 py-2 font-mono text-xs font-black text-slate-900"
                >
                  Dispatch Production Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
